import { useState, type FormEvent } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label as FieldLabel } from '@/components/ui/label';
import { CreateLabelError, useCreateLabel } from '@/features/labels';
// By path: neither helper is part of the labels package's public face.
import { normalizeForCompare } from '@/features/labels/labelName';
import { nextLabelSlot } from '@/features/labels/labelSlots';
import type { LabelColor, LabelView } from '@/lib/api/generated/model';

import { LabelColorChoice } from './LabelColorChoice';

/** The longest name the service accepts (openapi `CreateLabelRequest.name`). */
export const LABEL_NAME_MAX = 32;

function takenSentence(name: string): string {
  return `A label called “${name}” already exists.`;
}

export interface CreateLabelDialogProps {
  workspaceId: string;
  /** For the pre-chosen colour and the check before anything is sent. */
  registry: readonly LabelView[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * A new label, from Settings (spec 228 US2). Until now a label could only be
 * born from a task's Labels field, so the page that curates the vocabulary
 * could rename and delete words but never add one.
 *
 * The colour is chosen here, unlike the task picker's one-field create: this is
 * the page where someone decides what a label looks like. It starts on the
 * least-used slot — the one the backend would have picked — so accepting the
 * default is still a one-field act.
 */
export function CreateLabelDialog({
  workspaceId,
  registry,
  open,
  onOpenChange,
}: CreateLabelDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        {/* Keyed by opening, so each opening starts blank on the current least-used slot. */}
        {open ? (
          <CreateLabelForm
            workspaceId={workspaceId}
            registry={registry}
            onDone={() => {
              onOpenChange(false);
            }}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function CreateLabelForm({
  workspaceId,
  registry,
  onDone,
}: {
  workspaceId: string;
  registry: readonly LabelView[];
  onDone: () => void;
}) {
  const [name, setName] = useState('');
  const [color, setColor] = useState<LabelColor>(() => nextLabelSlot(registry));
  const [refusal, setRefusal] = useState<string | null>(null);
  const create = useCreateLabel();

  const trimmed = name.trim();
  const canSave =
    !create.isPending && trimmed.length > 0 && trimmed.length <= LABEL_NAME_MAX;

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!canSave) return;
    const taken = registry.some(
      (label) => normalizeForCompare(label.name) === normalizeForCompare(name),
    );
    // Refused here rather than by the server: the sentence is the same, and the
    // reader is told before a request they already know the answer to.
    if (taken) {
      setRefusal(takenSentence(trimmed));
      return;
    }
    setRefusal(null);
    create.mutate(
      { workspaceId, name: trimmed, color },
      {
        onSuccess: (label) => {
          toast.success(`${label.name} added`);
          onDone();
        },
        // The dialog stays open on the refused name: closing it would throw
        // away the word the reader now has to change.
        onError: (failure) => {
          setRefusal(
            failure instanceof CreateLabelError &&
              failure.refusal.kind === 'name-taken'
              ? takenSentence(trimmed)
              : failure.message,
          );
        },
      },
    );
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-4">
      <DialogHeader>
        <DialogTitle>New label</DialogTitle>
        <DialogDescription>
          A word tasks in this workspace can carry. Its colour can be changed
          later.
        </DialogDescription>
      </DialogHeader>

      <div className="flex flex-col gap-1.5">
        <FieldLabel htmlFor="new-label-name">Name</FieldLabel>
        <Input
          id="new-label-name"
          // The one field the reader came to fill.
          autoFocus
          value={name}
          maxLength={LABEL_NAME_MAX}
          aria-invalid={refusal ? true : undefined}
          aria-describedby={refusal ? 'new-label-refusal' : undefined}
          onChange={(event) => {
            setName(event.target.value);
            setRefusal(null);
          }}
        />
        {refusal ? (
          <p
            id="new-label-refusal"
            role="alert"
            className="text-destructive text-xs"
          >
            {refusal}
          </p>
        ) : null}
      </div>

      <LabelColorChoice
        name="new-label-color"
        value={color}
        onChange={setColor}
      />

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onDone}>
          Cancel
        </Button>
        <Button type="submit" disabled={!canSave}>
          {create.isPending ? 'Adding…' : 'Add label'}
        </Button>
      </DialogFooter>
    </form>
  );
}
